'use client';

import { useState } from 'react';
import type { Product } from '@/lib/shopify/types';

interface FilterSection {
  id: string;
  name: string;
  options: string[];
}

interface CollectionFiltersProps {
  products: Product[];
  selectedFilters: Record<string, string[]>;
  onFilterChange: (filterId: string, value: string) => void;
  onClearFilters: () => void;
  totalProducts: number;
  filteredCount: number;
}

export default function CollectionFilters({
  products,
  selectedFilters,
  onFilterChange,
  onClearFilters,
  filteredCount,
}: CollectionFiltersProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    size: true,
    color: true,
  });

  // Extract unique filter options from products
  const extractFilters = (): FilterSection[] => {
    const filters: FilterSection[] = [];

    // Size filter
    const sizes = new Set<string>();
    products.forEach((product) => {
      const sizeOption = product.options.find(
        (opt) => opt.name.toLowerCase() === 'size'
      );
      sizeOption?.values.forEach((v) => sizes.add(v));
    });
    if (sizes.size > 0) {
      filters.push({
        id: 'size',
        name: 'SIZE',
        options: Array.from(sizes).sort((a, b) => {
          const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
          const aIndex = sizeOrder.indexOf(a);
          const bIndex = sizeOrder.indexOf(b);
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          return a.localeCompare(b);
        }),
      });
    }

    // Color filter
    const colors = new Set<string>();
    products.forEach((product) => {
      const colorOption = product.options.find(
        (opt) => opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour'
      );
      colorOption?.values.forEach((v) => colors.add(v));
    });
    if (colors.size > 0) {
      filters.push({
        id: 'color',
        name: 'COLOR',
        options: Array.from(colors).sort(),
      });
    }

    // Product Type filter
    const productTypes = new Set<string>();
    products.forEach((product) => {
      if (product.productType) {
        productTypes.add(product.productType);
      }
    });
    if (productTypes.size > 0) {
      filters.push({
        id: 'productType',
        name: 'CATEGORY',
        options: Array.from(productTypes).sort(),
      });
    }

    // Vendor filter
    const vendors = new Set<string>();
    products.forEach((product) => {
      if (product.vendor) {
        vendors.add(product.vendor);
      }
    });
    if (vendors.size > 1) {
      filters.push({
        id: 'vendor',
        name: 'BRAND',
        options: Array.from(vendors).sort(),
      });
    }

    return filters;
  };

  const filters = extractFilters();

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const hasActiveFilters = Object.values(selectedFilters).some((arr) => arr.length > 0);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="pb-4 border-b border-[var(--color-border)]">
        <h2 className="text-sm font-bold uppercase tracking-wider">Filters</h2>
      </div>

      {/* Filter Sections */}
      <div className="flex-1 overflow-y-auto py-4">
        {filters.map((filter) => (
          <div key={filter.id} className="border-b border-[var(--color-border)]">
            <button
              onClick={() => toggleSection(filter.id)}
              className="flex items-center justify-between w-full py-4 text-left"
            >
              <span className="text-sm font-medium uppercase tracking-wide">
                {filter.name}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className={`w-4 h-4 transition-transform ${
                  expandedSections[filter.id] ? 'rotate-45' : ''
                }`}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </button>

            {expandedSections[filter.id] && (
              <div className="pb-4 space-y-2">
                {filter.options.map((option) => {
                  const isSelected = selectedFilters[filter.id]?.includes(option);
                  return (
                    <label
                      key={option}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <div
                        className={`w-4 h-4 border flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-black border-black'
                            : 'border-gray-300 group-hover:border-gray-400'
                        }`}
                      >
                        {isSelected && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={3}
                            stroke="white"
                            className="w-3 h-3"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M4.5 12.75l6 6 9-13.5"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-[var(--color-text-primary)]">
                        {option}
                      </span>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={isSelected}
                        onChange={() => onFilterChange(filter.id, option)}
                      />
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer Actions */}
      <div className="pt-4 border-t border-[var(--color-border)] flex gap-2">
        <button
          onClick={onClearFilters}
          disabled={!hasActiveFilters}
          className="flex-1 py-3 text-sm font-medium uppercase tracking-wide border border-[var(--color-border)] hover:border-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Clear
        </button>
        <button className="flex-1 py-3 text-sm font-medium uppercase tracking-wide bg-brand-blue text-white hover:bg-brand-blue-hover transition-colors">
          Apply ({filteredCount})
        </button>
      </div>
    </div>
  );
}
